import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { Check, Crown, Zap, Star, Rocket } from "lucide-react";
import { PricingCard } from "@/components/PricingCard";

interface Plan {
  code: string;
  name: string;
  description: string;
  price: number;
  credits: number;
  features: string[];
  isPopular?: boolean;
  razorpayPlanId?: string;
}

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  role?: string;
}

export default function UpgradePage() {
  const { data: user } = useQuery<UserData>({
    queryKey: ["/api/auth/me"],
  });

  const { data: plansData } = useQuery<{ plans: Plan[] }>({
    queryKey: ["/api/billing/plans"],
  });

  // Also fetch admin-configured pricing if available
  const { data: adminPricingData } = useQuery({
    queryKey: ["/api/admin/plan-pricing-configs"],
    enabled: true, // Fetch admin pricing to show dynamic pricing
  });

  // Function to transform admin pricing data to match expected format
  const transformAdminPricingToPlans = (adminPricing: any[]) => {
    if (!adminPricing || !Array.isArray(adminPricing)) return [];
    
    return adminPricing.map(config => ({
      code: config.plan_code,
      name: config.plan_name,
      monthlyPricePaise: config.monthly_price_paise,
      annualPricePaise: config.annual_price_paise,
      discountPercent: config.annual_discount_percent,
      monthlyCredits: config.monthly_credit_grant,
      features: config.features || [],
      addonCreditPrice: config.addon_credit_price,
      razorpayKeyId: config.razorpay_key_id,
      isActive: config.is_active
    }));
  };

  // Use admin pricing if available, otherwise fall back to API plans
  const plans = adminPricingData && Array.isArray(adminPricingData) 
    ? { plans: transformAdminPricingToPlans(adminPricingData) }
    : plansData;

  const currentPlan = user?.planCode || "FREE";

  const handleUpgrade = (planCode: string, interval: "month" | "annual" = "month") => {
    // Navigate to checkout with selected plan and billing interval
    window.location.href = `/checkout?plan=${planCode}&interval=${interval}`;
  };

  const getPlanIcon = (planCode: string) => {
    switch (planCode) {
      case "FREE": return <Star className="h-6 w-6" />;
      case "PRO": return <Zap className="h-6 w-6" />;
      case "ENTERPRISE": return <Rocket className="h-6 w-6" />;
      default: return <Star className="h-6 w-6" />;
    }
  };

  const getBenefits = () => [
    {
      title: "More Credits",
      description: "Get more monthly credits to power your AI writing",
      icon: <Zap className="h-5 w-5 text-yellow-500" />
    },
    {
      title: "Advanced Models",
      description: "Access to premium AI models like GPT-4 and Claude",
      icon: <Crown className="h-5 w-5 text-purple-500" />
    },
    {
      title: "Priority Support",
      description: "Get faster response times for help and support",
      icon: <Star className="h-5 w-5 text-blue-500" />
    },
    {
      title: "Advanced Features",
      description: "Unlock premium templates and advanced editing tools",
      icon: <Rocket className="h-5 w-5 text-green-500" />
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Upgrade Your Plan</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Unlock the full potential of AI-powered storytelling with advanced features and higher credit limits.
          </p>
          {currentPlan !== "FREE" && (
            <Badge variant="secondary" className="text-lg px-4 py-2">
              Current Plan: {currentPlan}
            </Badge>
          )}
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {getBenefits().map((benefit, index) => (
            <Card key={index} className="text-center">
              <CardContent className="pt-6">
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Plans */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-center">Choose Your Plan</h2>
          
          {plans?.plans ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.plans.map((plan) => (
                <PricingCard 
                  key={plan.code}
                  plan={plan}
                  isCurrentPlan={currentPlan === plan.code}
                  onSelectPlan={(interval) => handleUpgrade(plan.code, interval)}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Fallback pricing display while loading */}
              <Card className="relative">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Star className="h-6 w-6" />
                    <CardTitle>Free</CardTitle>
                  </div>
                  <CardDescription>Perfect for getting started</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold">₹0<span className="text-lg font-normal">/month</span></div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      500 monthly credits
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Basic AI models
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Community support
                    </li>
                  </ul>
                  <Button disabled={currentPlan === "FREE"} className="w-full">
                    {currentPlan === "FREE" ? "Current Plan" : "Downgrade"}
                  </Button>
                </CardContent>
              </Card>

              <Card className="relative border-primary">
                <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2" variant="default">
                  Most Popular
                </Badge>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Zap className="h-6 w-6" />
                    <CardTitle>Pro</CardTitle>
                  </div>
                  <CardDescription>For serious writers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold">₹99<span className="text-lg font-normal">/month</span></div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      5,000 monthly credits
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Premium AI models
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Priority support
                    </li>
                  </ul>
                  <Button 
                    onClick={() => handleUpgrade("PRO")}
                    disabled={currentPlan === "PRO"}
                    className="w-full"
                  >
                    {currentPlan === "PRO" ? "Current Plan" : "Upgrade to Pro"}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Rocket className="h-6 w-6" />
                    <CardTitle>Enterprise</CardTitle>
                  </div>
                  <CardDescription>For teams & publishing houses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold">₹499<span className="text-lg font-normal">/month</span></div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      500,000 monthly credits
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Team workspace & API access
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Dedicated support
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgrade("ENTERPRISE")}
                    disabled={currentPlan === "ENTERPRISE"}
                    className="w-full"
                  >
                    {currentPlan === "ENTERPRISE" ? "Current Plan" : "Upgrade to Enterprise"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <div className="space-y-6 mt-12">
          <h2 className="text-2xl font-bold text-center">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What are credits?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Credits are used to power AI features like story generation, editing suggestions, and blueprint creation. Different AI models consume different amounts of credits based on their capabilities.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I change plans anytime?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate the billing accordingly.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do unused credits roll over?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Monthly plan credits reset each billing cycle. However, any add-on credits you purchase separately will remain in your account until used.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  We accept all major credit cards, debit cards, and digital wallets through our secure payment processor Razorpay.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}